// Photos are made smaller before they are sent, as WhatsApp does (Gary, 16 Oct). A phone
// camera photo is often 4 to 12 MB; WhatsApp takes up to 5 MB. Shrunk to 1600px on its
// long side as a JPEG it is usually 300 KB to 1 MB and still sharp on a phone.
// Small photos, and anything the phone cannot draw, are sent as they are.

const LONG_SIDE = 1600;
const QUALITY = 0.8;
const SMALL_ENOUGH = 1024 * 1024;

export async function shrinkPhoto(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.size <= SMALL_ENOUGH) return file;
  try {
    const picture = await decode(file);
    if (!picture) return file;
    const scale = Math.min(1, LONG_SIDE / Math.max(picture.width, picture.height));
    const width = Math.round(picture.width * scale);
    const height = Math.round(picture.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) { picture.done(); return file; }
    // White under see-through parts, which a JPEG would otherwise turn black.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(picture.source, 0, 0, width, height);
    picture.done();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

interface Picture { source: CanvasImageSource; width: number; height: number; done: () => void }

/**
 * Opens the photo to draw it, the right way up. Newer phones take the quick way; older
 * iPhones (iOS 15 and 16) without it, or that refuse its options, use a plain image.
 */
async function decode(file: File): Promise<Picture | null> {
  if (typeof createImageBitmap === 'function') {
    for (const options of [{ imageOrientation: 'from-image' } as ImageBitmapOptions, undefined]) {
      try {
        const bitmap = await createImageBitmap(file, options);
        return { source: bitmap, width: bitmap.width, height: bitmap.height, done: () => bitmap.close() };
      } catch { /* try the next way */ }
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, done: () => URL.revokeObjectURL(url) };
  } catch {
    URL.revokeObjectURL(url);
    return null;
  }
}
